import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

interface ServiceRecord {
  id: string;
  date: Date;
  customerInfo: {
    name: string;
    phone: string;
    email: string;
  };
  vehicleInfo: {
    make: string;
    model: string;
    year: number;
    vin: string;
    licensePlate: string;
    mileage: number;
  };
  serviceType: 'maintenance' | 'repair' | 'diagnostic' | 'inspection';
  description: string;
  technician: string;
  parts: {
    name: string;
    partNumber: string;
    quantity: number;
    price: number;
  }[];
  laborHours: number;
  laborRate: number;
  totalCost: number;
  paymentStatus: 'paid' | 'unpaid' | 'partial';
  notes: string;
}

export const ServiceHistory: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState<ServiceRecord | null>(null);
  const [openRecordDialog, setOpenRecordDialog] = useState(false);

  // Mock service records data
  const serviceRecords: ServiceRecord[] = [
    {
      id: 'SRV-001',
      date: new Date(2023, 2, 15),
      customerInfo: {
        name: 'John Smith',
        phone: '(123) 456-7890',
        email: 'john.smith@example.com',
      },
      vehicleInfo: {
        make: 'Toyota',
        model: 'Corolla',
        year: 2019,
        vin: 'JT2BF22K1W0123456',
        licensePlate: 'ABC123',
        mileage: 35000,
      },
      serviceType: 'maintenance',
      description: 'Regular maintenance - oil change, filter replacement, and general inspection',
      technician: 'David Brown',
      parts: [
        {
          name: 'Oil Filter',
          partNumber: 'OF-67890',
          quantity: 1,
          price: 12.50,
        },
        {
          name: 'Synthetic Oil (5W-30)',
          partNumber: 'OIL-5W30',
          quantity: 5,
          price: 10.99,
        },
      ],
      laborHours: 1,
      laborRate: 85,
      totalCost: 149.95,
      paymentStatus: 'paid',
      notes: 'Customer requested OEM parts only',
    },
    {
      id: 'SRV-002',
      date: new Date(2023, 2, 10),
      customerInfo: {
        name: 'Sarah Johnson',
        phone: '(234) 567-8901',
        email: 'sarah.j@example.com',
      },
      vehicleInfo: {
        make: 'Honda',
        model: 'Civic',
        year: 2020,
        vin: '1HGCM82633A123456',
        licensePlate: 'XYZ789',
        mileage: 22000,
      },
      serviceType: 'repair',
      description: 'Brake pad replacement and rotor resurfacing',
      technician: 'Mike Williams',
      parts: [
        {
          name: 'Front Brake Pads',
          partNumber: 'BP-12345',
          quantity: 1,
          price: 45.99,
        },
        {
          name: 'Rear Brake Pads',
          partNumber: 'BP-67890',
          quantity: 1,
          price: 39.99,
        },
      ],
      laborHours: 2.5,
      laborRate: 85,
      totalCost: 298.48,
      paymentStatus: 'paid',
      notes: '',
    },
    {
      id: 'SRV-003',
      date: new Date(2023, 2, 5),
      customerInfo: {
        name: 'Michael Brown',
        phone: '(345) 678-9012',
        email: 'm.brown@example.com',
      },
      vehicleInfo: {
        make: 'BMW',
        model: 'X5',
        year: 2021,
        vin: 'WBAKJ4C51BC123456',
        licensePlate: 'LMN456',
        mileage: 15000,
      },
      serviceType: 'diagnostic',
      description: 'Check engine light investigation - found faulty oxygen sensor',
      technician: 'John Smith',
      parts: [
        {
          name: 'Oxygen Sensor',
          partNumber: 'OS-54321',
          quantity: 1,
          price: 120.00,
        },
      ],
      laborHours: 1.5,
      laborRate: 95,
      totalCost: 262.50,
      paymentStatus: 'unpaid',
      notes: 'Customer approved up to $300 for repairs',
    },
  ];

  const handleOpenRecordDialog = (record: ServiceRecord) => {
    setSelectedRecord(record);
    setOpenRecordDialog(true);
  };

  const handleCloseRecordDialog = () => {
    setOpenRecordDialog(false);
    setSelectedRecord(null);
  };

  const getServiceTypeIcon = (type: string) => {
    switch (type) {
      case 'maintenance':
        return <BuildIcon fontSize="small" />;
      case 'repair':
        return <BuildIcon fontSize="small" />;
      case 'diagnostic':
        return <BuildIcon fontSize="small" />;
      case 'inspection':
        return <BuildIcon fontSize="small" />;
      default:
        return <BuildIcon fontSize="small" />;
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'unpaid':
        return 'error';
      case 'partial':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toFixed(2)}`;
  };

  const filteredRecords = serviceRecords.filter(record => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      record.customerInfo.name.toLowerCase().includes(query) ||
      record.vehicleInfo.make.toLowerCase().includes(query) ||
      record.vehicleInfo.model.toLowerCase().includes(query) ||
      record.vehicleInfo.licensePlate.toLowerCase().includes(query) ||
      record.vehicleInfo.vin.toLowerCase().includes(query) ||
      record.description.toLowerCase().includes(query)
    );
  });

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Service History
        </Typography>

        <Grid container spacing={3}>
          {/* Search and Filter */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Search by customer, vehicle, or service description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button startIcon={<FilterIcon />} sx={{ mr: 1 }}>
                    Filter
                  </Button>
                  <Button startIcon={<PrintIcon />}>
                    Export
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Service Records Table */}
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Service ID</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Vehicle</TableCell>
                    <TableCell>Service Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Technician</TableCell>
                    <TableCell>Total Cost</TableCell>
                    <TableCell>Payment Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRecords.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>{record.id}</TableCell>
                      <TableCell>{formatDate(record.date)}</TableCell>
                      <TableCell>{record.customerInfo.name}</TableCell>
                      <TableCell>
                        {record.vehicleInfo.make} {record.vehicleInfo.model} (
                        {record.vehicleInfo.year})
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getServiceTypeIcon(record.serviceType)}
                          label={
                            record.serviceType.charAt(0).toUpperCase() + record.serviceType.slice(1)
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {record.description.length > 30
                          ? `${record.description.substring(0, 30)}...`
                          : record.description}
                      </TableCell>
                      <TableCell>{record.technician}</TableCell>
                      <TableCell>{formatCurrency(record.totalCost)}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            record.paymentStatus.charAt(0).toUpperCase() +
                            record.paymentStatus.slice(1)
                          }
                          color={getPaymentStatusColor(record.paymentStatus) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenRecordDialog(record)}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>

        {/* Service Record Dialog */}
        <Dialog
          open={openRecordDialog}
          onClose={handleCloseRecordDialog}
          maxWidth="md"
          fullWidth
        >
          {selectedRecord && (
            <>
              <DialogTitle>
                Service Record: {selectedRecord.id}
              </DialogTitle>
              <DialogContent>
                <Box sx={{ pt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <PersonIcon sx={{ mr: 1 }} />
                            <Typography variant="h6">Customer Information</Typography>
                          </Box>
                          <Typography variant="body1">
                            {selectedRecord.customerInfo.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedRecord.customerInfo.phone}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedRecord.customerInfo.email}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <CarIcon sx={{ mr: 1 }} />
                            <Typography variant="h6">Vehicle Information</Typography>
                          </Box>
                          <Typography variant="body1">
                            {selectedRecord.vehicleInfo.make} {selectedRecord.vehicleInfo.model} (
                            {selectedRecord.vehicleInfo.year})
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            VIN: {selectedRecord.vehicleInfo.vin}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            License Plate: {selectedRecord.vehicleInfo.licensePlate}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Mileage: {selectedRecord.vehicleInfo.mileage} miles
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <BuildIcon sx={{ mr: 1 }} />
                            <Typography variant="h6">Service Details</Typography>
                          </Box>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2" color="text.secondary">
                                Service Type
                              </Typography>
                              <Typography variant="body1">
                                {selectedRecord.serviceType.charAt(0).toUpperCase() +
                                  selectedRecord.serviceType.slice(1)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2" color="text.secondary">
                                Service Date
                              </Typography>
                              <Typography variant="body1">
                                {formatDate(selectedRecord.date)}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2" color="text.secondary">
                                Technician
                              </Typography>
                              <Typography variant="body1">
                                {selectedRecord.technician}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2" color="text.secondary">
                                Labor Hours
                              </Typography>
                              <Typography variant="body1">
                                {selectedRecord.laborHours} hours @ {formatCurrency(selectedRecord.laborRate)}/hr
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                Description
                              </Typography>
                              <Typography variant="body1">
                                {selectedRecord.description}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                Notes
                              </Typography>
                              <Typography variant="body1">
                                {selectedRecord.notes || 'No notes'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Parts Used</Typography>
                          </Box>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Part Name</TableCell>
                                  <TableCell>Part Number</TableCell>
                                  <TableCell align="right">Quantity</TableCell>
                                  <TableCell align="right">Unit Price</TableCell>
                                  <TableCell align="right">Subtotal</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {selectedRecord.parts.map((part, index) => (
                                  <TableRow key={index}>
                                    <TableCell>{part.name}</TableCell>
                                    <TableCell>{part.partNumber}</TableCell>
                                    <TableCell align="right">{part.quantity}</TableCell>
                                    <TableCell align="right">
                                      {formatCurrency(part.price)}
                                    </TableCell>
                                    <TableCell align="right">
                                      {formatCurrency(part.price * part.quantity)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6">Cost Summary</Typography>
                            <Chip
                              label={
                                selectedRecord.paymentStatus.charAt(0).toUpperCase() +
                                selectedRecord.paymentStatus.slice(1)
                              }
                              color={getPaymentStatusColor(selectedRecord.paymentStatus) as any}
                            />
                          </Box>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Parts Total:
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body1" align="right">
                                {formatCurrency(
                                  selectedRecord.parts.reduce(
                                    (sum, part) => sum + part.price * part.quantity,
                                    0
                                  )
                                )}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Labor Cost:
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body1" align="right">
                                {formatCurrency(selectedRecord.laborHours * selectedRecord.laborRate)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="h6">Total:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="h6" align="right">
                                {formatCurrency(selectedRecord.totalCost)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button startIcon={<PrintIcon />}>Print</Button>
                <Button startIcon={<EmailIcon />}>Email</Button>
                <Button onClick={handleCloseRecordDialog}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}; 